import React, { useState } from 'react';
import { IconType } from 'react-icons';
import { Switch } from '@headlessui/react';
import { FaDownload } from 'react-icons/fa';
import { AiFillStar } from 'react-icons/ai';
import Link from 'components/Link';
import SectionTitle from 'components/SectionTitle';
import Modal from 'components/Modal/HeadlessUiModal';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useWatchlist } from 'hooks/useWatchlist';
import { getCalendarPage } from 'constants/pages';
import TooltipComponent from 'components/Tooltip';
import WatchList from '../components/WatchListModal';

interface SectionHeaderProps {
  title: string;
  Icon: IconType;
  isWatchListOnly: boolean;
  setIsWatchListOnly: (value: boolean) => void;
  paginationInfo?: any;
  dataLength: number;
  showWatchlistToggle?: boolean;
  slug?: string;
  symbol?: string;
  screenType?: string;
  handleDownload?: () => void;
  handlePremiumFeature?: (title: string) => boolean;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  slug,
  title,
  symbol,
  Icon,
  handleDownload,
  handlePremiumFeature,
  screenType,
  isWatchListOnly,
  setIsWatchListOnly,
  paginationInfo,
  dataLength,
  showWatchlistToggle = true,
}) => {
  const { user: currentUser } = useCurrentUser();

  const [isWatchListModalOpen, setIsWatchListModalOpen] = useState(false);

  const { watchListRefetch } = useWatchlist();

  const displayText = paginationInfo
    ? `Showing ${paginationInfo.startRecord} to ${paginationInfo.endRecord} of ${paginationInfo.totalRecords} results`
    : `Showing ${dataLength} results`;

  const handleOpenWatchListModal = () => {
    if (handlePremiumFeature && handlePremiumFeature('Log in to view watchlist')) {
      return;
    }

    if (!handlePremiumFeature && !currentUser?.id) {
      return;
    }

    setIsWatchListModalOpen(true);
    void watchListRefetch().catch((error) => {
      console.error('Failed to refresh watchlist:', error);
    });
  };

  const handleWatchListToggle = (value: boolean) => {
    if (value && handlePremiumFeature && handlePremiumFeature('Log in to filter by watchlist')) {
      return;
    }

    if (value && !handlePremiumFeature && !currentUser?.id) {
      return;
    }

    setIsWatchListOnly(value);
  };

  const handleCloseWatchListModal = () => {
    setIsWatchListModalOpen(false);
  };

  return (
    <>
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0">
        <div className="flex flex-col gap-2 sm:flex-1 sm:flex-row sm:items-center sm:gap-0">
          <SectionTitle Icon={Icon} title={title} style={{ marginBottom: 0 }} />
          {showWatchlistToggle && !symbol && (
            <div className="flex items-center gap-1">
              <Switch.Group as="div" className="flex items-center sm:ml-4">
                <Switch
                  checked={isWatchListOnly}
                  onChange={handleWatchListToggle}
                  className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                    pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out`}
                  />
                  <span
                    aria-hidden="true"
                    className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                      isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                    } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                  />
                </Switch>
                <div className={`ml-2 text-xs`}>
                  <TooltipComponent title="Update your watchlist" position="right">
                    <button
                      type="button"
                      onClick={handleOpenWatchListModal}
                      className="flex cursor-pointer items-center whitespace-nowrap rounded-[4px] border border-gray-300 bg-gray-50 px-0.5 py-0.5 text-xs font-semibold text-yellow-500 shadow-sm transition-colors hover:bg-gray-100 active:bg-gray-200 sm:px-1 sm:py-1"
                    >
                      <AiFillStar
                        aria-hidden="true"
                        className={`h-3.5 w-3.5 sm:h-auto sm:w-auto ${
                          isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'
                        }`}
                      />
                    </button>
                  </TooltipComponent>
                </div>
              </Switch.Group>
            </div>
          )}
        </div>
        {symbol && (
          <div className="flex w-full items-center justify-center gap-4 sm:flex-1 sm:gap-4">
            <div className="inline-flex items-center rounded-lg bg-palette-black-2 px-4 py-1 text-sm text-white">
              <span className="font-medium">Viewing data for: {symbol}</span>
              <Link
                href={getCalendarPage(screenType || '')}
                className="ml-2 text-white hover:text-blue-800 dark:text-blue-300 "
              >
                ✕
              </Link>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 sm:flex-1 sm:justify-end sm:gap-4">
          <div className="flex items-center">
            <span className="truncate text-xs text-gray-400">
              <span className="hidden sm:inline">{displayText}</span>
              <span className="sm:hidden">
                {paginationInfo
                  ? `${paginationInfo.totalRecords} results`
                  : `${dataLength} results`}
              </span>
            </span>
          </div>
          <div className="flex items-center">
            <button
              onClick={handleDownload}
              className="flex h-5 items-center justify-center whitespace-nowrap rounded-[4px] bg-white/10 px-1.5 text-[.6rem] shadow-sm sm:h-6 sm:px-2 sm:text-[.65rem]"
            >
              <FaDownload className="h-2.5 w-2.5 sm:h-auto sm:w-auto" />
              <span className="ml-1 text-[.55rem] font-semibold sm:ml-2 sm:text-[.6rem]">
                <span className="xs:inline hidden">Download</span>
                <span className="xs:hidden">Download</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isWatchListModalOpen}
        classNameMaxWidth={'max-w-2xl'}
        handleClose={handleCloseWatchListModal}
      >
        <div className="flex w-full  flex-col">
          <WatchList onClose={handleCloseWatchListModal} slug={slug} />
        </div>
      </Modal>
    </>
  );
};
